import { useState, useEffect, useCallback } from 'react';
import { billingService } from '../services/billingService';
import { BillingSettings } from '../types/billing';
import { useAuth } from '../contexts/AuthContext';

export const useBillingSettings = () => {
  const [billingSettings, setBillingSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchBillingSettings = async () => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const settings = await billingService.getBillingSettings(user.organizationId);
      setBillingSettings(settings);
    } catch (err) {
      console.error('Error fetching billing settings:', err);
      setError('Nem sikerült betölteni a számlázási beállításokat');
    } finally {
      setLoading(false);
    }
  };

  const createBillingSettings = async (settings: Omit<BillingSettings, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const id = await billingService.createBillingSettings(settings);
      const newSettings: BillingSettings = {
        id,
        ...settings,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setBillingSettings(newSettings);
    } catch (err) {
      console.error('Error creating billing settings:', err);
      setError('Nem sikerült létrehozni a számlázási beállításokat');
    } finally {
      setLoading(false);
    }
  };

  const updateBillingSettings = async (updates: Partial<BillingSettings>) => {
    if (!billingSettings?.id) {
      throw new Error('Nincs beállítás ID');
    }

    try {
      setError(null);
      await billingService.updateBillingSettings(billingSettings.id, updates);
      setBillingSettings(prev => prev ? { ...prev, ...updates, updatedAt: new Date() } : null);
    } catch (err) {
      console.error('Error updating billing settings:', err);
      setError('Nem sikerült frissíteni a számlázási beállításokat');
      throw err;
    }
  };

  const deleteBillingSettings = async () => {
    if (!billingSettings?.id) {
      throw new Error('Nincs beállítás ID');
    }

    try {
      setError(null);
      await billingService.deleteBillingSettings(billingSettings.id);
      setBillingSettings(null);
    } catch (err) {
      console.error('Error deleting billing settings:', err);
      setError('Nem sikerült törölni a számlázási beállításokat');
      throw err;
    }
  };

  const validateAgentKey = async (agentKey: string, provider: 'szamlazz' | 'billingo' = 'szamlazz'): Promise<{valid: boolean, message: string}> => {
    try {
      setError(null);
      const result = await billingService.validateAgentKey(agentKey, provider);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      return {
        valid: false,
        message: errorMessage
      };
    }
  };

  const getDefaultSettings = useCallback((): Omit<BillingSettings, 'id' | 'createdAt' | 'updatedAt'> => {
    if (!user?.organizationId) {
      throw new Error('No organization ID available');
    }
    return billingService.getDefaultSettings(user.organizationId);
  }, [user?.organizationId]);

  useEffect(() => {
    fetchBillingSettings();
  }, [user?.organizationId]);

  return {
    billingSettings,
    loading,
    error,
    fetchBillingSettings,
    createBillingSettings,
    updateBillingSettings,
    deleteBillingSettings,
    validateAgentKey,
    getDefaultSettings
  };
}; 